import { useCallback, useRef } from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'
import type { Swiper as SwiperType } from 'swiper'

import { useThrottle } from '@/hooks/useThrottle'
import { isPointerMode } from '@/utils/utils'

export const useLinePointerNavigation = ({
  nodeId,
  swiper,
  setActiveSlideIndex,
}: {
  nodeId: string
  swiper: Nullable<SwiperType>
  setActiveSlideIndex: (activeSlideIndex: number) => void
}) => {
  const setFocus = useSetFocus()
  const focusNode = useFocusNodeById(nodeId)

  const onMouseHandler = (focusId: string) => {
    if (!swiper || !focusNode) {
      return
    }
    const activeSlideIndex = +(focusId.split('_').pop() || 0)
    setActiveSlideIndex(activeSlideIndex)
    swiper.slideTo(activeSlideIndex, 0)
  }

  const throttledProcessKeyUp = useThrottle(onMouseHandler, 300)

  const onMouseOverTile = useCallback(
    (_: any, focusId: string) => throttledProcessKeyUp(focusId),
    [throttledProcessKeyUp],
  )

  const mouseOverIntervalId = useRef(0)

  const startSlideNavigation = useCallback(() => {
    setFocus(nodeId)
    if (!swiper || !swiper.activeIndex) {
      return
    }
    setActiveSlideIndex(swiper.activeIndex - 1)
    swiper.slidePrev()
    clearTimeout(mouseOverIntervalId.current)

    mouseOverIntervalId.current = window.setInterval(() => {
      if (swiper.activeIndex) {
        setActiveSlideIndex(swiper.activeIndex - 1)
        swiper.slidePrev()
      } else {
        stopSlideNavigation()
      }
    }, 300)
  }, [swiper])

  const stopSlideNavigation = () => {
    clearInterval(mouseOverIntervalId.current)
  }

  return {
    startSlideNavigation,
    stopSlideNavigation,
    onMouseOverTile: isPointerMode ? onMouseOverTile : undefined,
  }
}
